import { Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { IamUsersApiService, IamUsersApiServiceObservable } from '@kempoweroyj/ngx-iam-users-api-client';
import { CeInlineBannerComponent, CeLevelMenu } from '@kempower-nx-workspace/shared/kp-design-system';
import { SidePanelLayoutComponent, SidePanelLayoutService } from '@kempower-nx-workspace/layout-templates';
import { IamOrganizationsApiServiceObservable } from '@kempoweroyj/ngx-iam-organizations-api-client';
import { environment } from '@kempower-nx-workspace/ce-environment';
import { AuthenticationService } from '@kempower-nx-workspace/ce-authentication';
import { CeFeatureFlag, FeatureFlagLocalMap, FeatureFlagService } from '@kempower-nx-workspace/chargeye-shared';
import { LICENSE_CORE, LicenseInfoService, licenseIsValid, LicenseType, PermissionsInfoService } from '@kempower-nx-workspace/chargeye-licensing';
import { CoreSideNavOrgSwitchComponent } from '../ui/side-nav-org-switch/side-nav-org-switch.component';
import { CoreSideNavProfileOptionsComponent } from '../ui/side-nav-profile-options/side-nav-profile-options.component';
import { CoreSideNavMenuComponent } from '../ui/side-nav-menu/side-nav-menu.component';
import { SidePanelNavigationService } from '../data-access/side-panel-navigation.service';
import { Permissions } from '@kempoweroyj/ce-lib-iam';
import { get as _get } from 'lodash-es';

type CeLevelMenuExtended = CeLevelMenu & {
    data?: {
        featureFlag?: CeFeatureFlag;
        hideWhenFeatureFlag?: CeFeatureFlag;
        redirectTo?: string;
        requiredLicenseLevel?: LicenseType;
        coreLicenseAllowed?: boolean;
    };
};

@Component({
    selector: 'ce-core-side-nav-panel',
    imports: [
        CommonModule,
        CeInlineBannerComponent,
        RouterModule,
        SidePanelLayoutComponent,
        CoreSideNavOrgSwitchComponent,
        CoreSideNavProfileOptionsComponent,
        CoreSideNavMenuComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    providers: [
        IamUsersApiService,
        IamUsersApiServiceObservable,
        IamOrganizationsApiServiceObservable,
        SidePanelLayoutService,
    ],
    templateUrl: './side-panel.component.html'
})
export class SidePanelComponent {
    classicUrl = `https://${environment.appConfig.apiBaseUrl}`;

    readonly #route: ActivatedRoute = inject(ActivatedRoute);
    readonly #authService = inject(AuthenticationService);
    readonly #sidePanelNavigationService = inject(SidePanelNavigationService);
    readonly #iamUsersApiService = inject(IamUsersApiServiceObservable);
    readonly #iamOrganizationsApiServiceObservable = inject(IamOrganizationsApiServiceObservable);
    readonly #licenseInfoService = inject(LicenseInfoService);
    readonly #permissionsInfoService = inject(PermissionsInfoService);
    readonly #featureFlagService = inject(FeatureFlagService);

    /**
     * Convert obs to signals
     */
    hasTenancies = toSignal(this.#iamUsersApiService.getMyTenancyAccess());

    routeMenu = toSignal(this.#sidePanelNavigationService.getRouteMenu$(this.#route), { initialValue: [] });

    activeLicense = toSignal(this.#licenseInfoService.getActiveLicense$());

    activePermissions = toSignal(this.#permissionsInfoService.getActivePermissions$());

    organisationName = toSignal(
        this.#iamOrganizationsApiServiceObservable.getMyOrganization({ minimized: true }).pipe(
            map((data) => {
                if (typeof data === 'object' && data !== null && 'name' in data) {
                    return data.name ?? '';
                }
                return '';
            })
        ),
        { initialValue: '' }
    );

    /**
     * Computed signals
     */

    userName = computed((): string => {
        return this.#authService.profileStore()?.name || '';
    });

    navigationListComputed = computed(() => {
        let routeMenu = this.routeMenu() as CeLevelMenu[];
        const activeLicense = this.activeLicense() as LicenseType;
        const activePermissions = this.activePermissions() as Permissions;
        if (activeLicense === null || activePermissions === null) {
            return [];
        }

        if (!routeMenu || routeMenu === undefined) {
            console.warn('routeMenu is undefined or null');
            routeMenu = [];
        }

        const notificationIcon = this.#sidePanelNavigationService.hasNewNotification() ? 'bell-new' : 'bell';

        routeMenu.forEach((item: CeLevelMenu) => {
            if (item?.label === 'Notifications') {
                item.icon = notificationIcon;
            }
        });

        const productUpdateCount = this.#sidePanelNavigationService.newProductUpdateCount();
        routeMenu.forEach((item: CeLevelMenu) => {
            if (item.children?.some((child: CeLevelMenu) => child.label === 'Product updates')) {
                item.icon = productUpdateCount > 0 ? 'help-new' : 'help';
            }
            item.children?.forEach((child: CeLevelMenu) => {
                if (child.label === 'Product updates') {
                    child.secondaryLabel = productUpdateCount > 0 ? { type: 'dot' } : null;
                }
            });
        });

        routeMenu = this.#filterFeatureFlag(routeMenu);

        routeMenu = this.#filterRouteByLicenseLevel(routeMenu, activeLicense);

        routeMenu = this.#filterRouteByPermissions(routeMenu, activePermissions);

        if (activeLicense === LICENSE_CORE) {
            routeMenu = this.#filterByCoreLicenseAllowed(routeMenu);
        }

        return routeMenu;
    });

    /**
     * For Kempower-license (CORE) users, keep only the routes whose `data.coreLicenseAllowed`
     * is explicitly `true`. Everything else is hidden — mirrors the `MyKempowerLicenseGuard`
     * route allowlist so users don't see items they can't reach.
     */
    #filterByCoreLicenseAllowed(routeMenu: CeLevelMenu[]): CeLevelMenu[] {
        const filterRecursive = (items: CeLevelMenu[]): CeLevelMenu[] =>
            items
                .filter((item: CeLevelMenuExtended) => item.data?.coreLicenseAllowed === true)
                .map((item) => ({
                    ...item,
                    ...(item.children && { children: filterRecursive(item.children) }),
                }));

        return filterRecursive(routeMenu);
    }

    /**
     * Utilities could be moved to utils file
     */

    /**
     * Filters a list of route menu items based on feature flags stored in local storage.
     *
     * This method retrieves the currently available feature flags from the `FeatureFlagService`
     * and removes any menu items (and their children) whose `data.featureFlag` property is either:
     *   - Not defined (kept by default)
     *   - Defined but disabled in the stored feature flags
     *
     * @param {CeLevelMenu[]} routeMenu - The list of menu items to filter.
     * @returns {CeLevelMenu[]} The filtered list of menu items where all feature-flagged items are enabled.
     *
     * @example
     * // Given featureFlags = { betaFeature: true, hiddenFeature: false }
     * filterFeatureFlag([
     *   { data: { featureFlag: 'betaFeature' }, children: [] },   // ✅ kept
     *   { data: { featureFlag: 'hiddenFeature' }, children: [] }, // ❌ removed
     *   { data: {}, children: [] }                                // ✅ kept
     * ]);
     */
    #filterFeatureFlag(routeMenu: CeLevelMenu[]): CeLevelMenu[] {
        const featureFlags = this.#featureFlagService.getFeatureFlagsInStorage();

        const filterRecursive = (items: CeLevelMenu[]): CeLevelMenu[] =>
            items
                .filter((item: CeLevelMenuExtended) => {
                    // Hide item if its hideWhenFeatureFlag is enabled
                    const hideFlag = item.data?.hideWhenFeatureFlag as CeFeatureFlag | undefined;
                    if (hideFlag && featureFlags?.[hideFlag]) {
                        return false;
                    }
                    return featureFlags === undefined
                        ? !item.data?.featureFlag
                        : this.hasFeatureFlag(item.data?.featureFlag, featureFlags);
                })
                .map((item) => ({
                    ...item,
                    ...(item.children && { children: filterRecursive(item.children) }),
                }));

        return filterRecursive(routeMenu);
    }

    /**
     * Determines whether a given feature flag is enabled.
     *
     * If the `featureFlag` is `undefined`, the function returns `true`.
     * If the `featureFlag` is a string, it checks if the corresponding entry in `featureFlags` is `true`.
     *
     * @param featureFlag - The feature flag to check, or `undefined` to indicate no specific flag.
     * @param featureFlags - A map of feature flag names to their enabled/disabled state.
     * @returns `true` if the feature flag is enabled or not specified; otherwise, `false`.
     */
    hasFeatureFlag(featureFlag: CeFeatureFlag | undefined, featureFlags: FeatureFlagLocalMap): boolean {
        return !featureFlag || (typeof featureFlag === 'string' && featureFlags[featureFlag] === true);
    }

    /**
     * Filters a list of route menu items based on the user's active license level.
     *
     * First, filters out root-level items the user is not allowed to access.
     * Then, for the remaining items, filters their children using the same license rules.
     *
     * @param routeObject - The full list of menu routes to filter.
     * @param activeLicense - The currently active license for the user.
     * @returns A new list of routes with only the allowed root items and children.
     */
    #filterRouteByLicenseLevel(routeObject: CeLevelMenu[], activeLicense: LicenseType): CeLevelMenu[] {
        const filterRecursive = (items: CeLevelMenu[]): CeLevelMenu[] =>
            items
                .filter((item: CeLevelMenu) => this.#routeHasLicenseLevel(item, activeLicense))
                .map((item: CeLevelMenu) => ({
                    ...item,
                    ...(item.children && { children: filterRecursive(item.children) }),
                }));

        return filterRecursive(routeObject);
    }

    /**
     * Determines whether the given route object has the required permissions based on the active permissions.
     *
     * Checks if the route's `requiredPermissions` are present and verifies if any of them are granted in `activePermissions`.
     * Returns `true` if at least one required permission is granted, or if no required permissions are specified for the route.
     *
     * @param routeObject - The route object containing permission requirements.
     * @param activePermissions - The currently active permissions to check against.
     * @returns `true` if the route can be accessed with the active permissions, otherwise `false`.
     */
    #routeHasPermission(routeObject: CeLevelMenuExtended, activePermissions: Permissions): boolean {
        const routeDataPermissions = routeObject?.data?.requiredPermissions;

        if (routeDataPermissions) {
            if (!activePermissions) return false;

            let hasAccess = true;
            for (const permissionAccess of routeDataPermissions) {
                if (!_get(activePermissions, permissionAccess)) {
                    hasAccess = false;
                    break;
                }
            }
            return hasAccess;
        }
        return routeDataPermissions === undefined;
    }

    /**
     * Filters a list of route objects based on the provided active permissions.
     * Only routes and their children that the user has permission to access are included in the result.
     *
     * @param routeObject - An array of `CeLevelMenu` objects representing the routes to filter.
     * @param activePermissions - The current user's permissions used to determine route accessibility.
     * @returns An array of `CeLevelMenu` objects, each containing only the routes and children permitted by the active permissions.
     */
    #filterRouteByPermissions(routeObject: CeLevelMenu[], activePermissions: Permissions): CeLevelMenu[] {
        const filterRecursive = (items: CeLevelMenu[]): CeLevelMenu[] =>
            items
                .filter((item: CeLevelMenu) => this.#routeHasPermission(item, activePermissions))
                .map((item: CeLevelMenu) => ({
                    ...item,
                    ...(item.children && { children: filterRecursive(item.children) }),
                }));

        return filterRecursive(routeObject);
    }

    /**
     * Determines whether a given route is accessible based on the user's active license.
     *
     * @param routeObject - A route menu item to evaluate.
     * @param activeLicense - The currently active license for the user.
     * @returns `true` if no license is required or the user's license meets the requirement, otherwise `false`.
     */
    #routeHasLicenseLevel(routeObject: CeLevelMenuExtended, activeLicense: LicenseType): boolean {
        const routeDataLicense = routeObject?.data?.requiredLicenseLevel;
        if (routeDataLicense) {
            return licenseIsValid(activeLicense, routeDataLicense as LicenseType);
        }
        return routeDataLicense === undefined;
    }
}
